






















import { Component, Prop, Vue } from 'vue-property-decorator';

import {
	createSortingState,
	MmPaginationState,
	MmSortingState,
	readAntSorter,
} from '../api/pagination';
import { MM_PAGE_SIZE_DEFAULT, MM_PAGE_SIZE_OPTIONS } from '../config/env';

export interface MmTableChangeEvent {
	pagination: MmPaginationState;
	sorter: MmSortingState;
	filters: { [key: string]: any };
}

@Component
export default class MmTable extends Vue {
	@Prop({ default: () => [] }) readonly columns!: any[];

	@Prop({ default: () => [] }) readonly dataSource!: any[];

	@Prop({ default: false }) readonly loading!: boolean;

	@Prop({ default: null }) readonly pagination!: MmPaginationState | null;

	@Prop({ default: 'id' }) readonly rowKey!: string | ((record: any, index: number) => string);

	/** Горизонтальная прокрутка несовместима с раскрытием строк */
	@Prop({ default: null }) readonly scrollX!: number | string | null;

	@Prop({ default: '50vh' }) readonly scrollY!: number | string | null;

	@Prop({ default: false }) readonly expandable!: boolean;

	/** null — раскрытием управляет сам ant; передав массив, его обязан обновлять вызывающий */
	@Prop({ default: null }) readonly expandedRowKeys!: any[] | null;

	@Prop({ default: 'middle' }) readonly size!: string;

	@Prop({ default: true }) readonly bordered!: boolean;

	@Prop({ default: true }) readonly showSizeChanger!: boolean;

	@Prop({ default: null }) readonly rowClassName!: ((record: any, index: number) => string) | null;

	@Prop({ default: null }) readonly customRow!: ((record: any, index: number) => any) | null;

	/** Передаём в ant, только когда задан: ant смотрит на наличие ключа, а null ломает индекс */
	@Prop({ default: null }) readonly expandIconColumnIndex!: number | null;

	@Prop({ default: null }) readonly expandIconAsCell!: boolean | null;

	@Prop({ default: '' }) readonly emptyText!: string;

	mounted(): void {
		this.warnAboutBrokenExpandSetup();
	}

	/* Методы, а не геттеры: $scopedSlots не реактивен, кэш геттера заморозил бы слоты первого рендера */

	buildColumns(): any[] {
		return this.resolveColumnSlots(this.columns ? this.columns : []);
	}

	buildPagination(): any {
		const state = this.pagination;

		if (state === null) {
			return false;
		}

		const self = this;

		return {
			current: state.current,
			pageSize: state.pageSize,
			total: state.total,
			/** size у пагинации задаём явно: иначе ant подставляет small по плотности таблицы */
			size: 'default',
			showSizeChanger: this.showSizeChanger,
			// Копия: ant кладёт объект пагинации в своё состояние и мутирует его.
			pageSizeOptions: MM_PAGE_SIZE_OPTIONS.slice(),
			showLessItems: true,
			showTotal: function showTotal(total: number, range: number[]): string {
				return self.formatTotal(total, range);
			},
		};
	}

	buildScroll(): any {
		const scroll: any = {};

		if (this.scrollX !== null && this.scrollX !== undefined && this.scrollX !== '') {
			scroll.x = this.scrollX;
		}

		if (this.scrollY !== null && this.scrollY !== undefined && this.scrollY !== '') {
			scroll.y = this.scrollY;
		}

		return scroll;
	}

	buildLocale(): any {
		const self = this;

		return {
			// emptyText функцией, а не узлом: она считается при отрисовке и видит текущий loading и слот
			emptyText: function emptyText(): any {
				return self.renderEmptyState();
			},
		};
	}

	/** ant считает ключ заданным даже со значением undefined — подставляем свойства только по факту */
	buildOptionalProps(): any {
		const props: any = {};

		if (this.rowClassName !== null) {
			props.rowClassName = this.rowClassName;
		}

		if (this.customRow !== null) {
			props.customRow = this.customRow;
		}

		if (this.expandIconColumnIndex !== null) {
			props.expandIconColumnIndex = this.expandIconColumnIndex;
		}

		if (this.expandIconAsCell !== null) {
			props.expandIconAsCell = this.expandIconAsCell;
		}

		return props;
	}

	/** Без слота отдаём undefined: любое другое значение включит разворачиватель у каждой строки */
	resolveExpandedRowRender(): any {
		if (!this.expandable) {
			return undefined;
		}

		return this.$scopedSlots.mmExpandedRow;
	}

	resolveExpandIcon(): any {
		if (!this.expandable) {
			return undefined;
		}

		return this.$scopedSlots.mmExpandIcon;
	}

	handleChange(pagination: any, filters: any, sorter: any): void {
		const antPagination: any = pagination ? pagination : {};
		const antSorter: any = sorter ? sorter : {};
		const current: MmPaginationState | null = this.pagination;

		const nextPagination: MmPaginationState = {
			current: typeof antPagination.current === 'number' ? antPagination.current : 1,
			pageSize: typeof antPagination.pageSize === 'number'
				? antPagination.pageSize
				: (current !== null ? current.pageSize : MM_PAGE_SIZE_DEFAULT),
			total: current !== null ? current.total : 0,
		};

		// У колонки без dataIndex ant кладёт в сортер только columnKey, иначе сортировка теряется
		const sorterField = antSorter.field !== undefined && antSorter.field !== null
			? antSorter.field
			: antSorter.columnKey;

		const payload: MmTableChangeEvent = {
			pagination: nextPagination,
			sorter: readAntSorter({ field: sorterField, order: antSorter.order }, createSortingState()),
			filters: filters ? filters : {},
		};

		this.$emit('change', payload);
	}

	handleExpand(expanded: boolean, record: any): void {
		this.$emit('expand', expanded, record);
	}

	handleExpandedRowKeysChange(keys: any[]): void {
		this.$emit('update:expandedRowKeys', keys);
	}

	private resolveColumnSlots(source: any[]): any[] {
		const result: any[] = [];

		for (let i = 0; i < source.length; i += 1) {
			result.push(this.resolveColumn(source[i]));
		}

		return result;
	}

	private resolveColumn(column: any): any {
		const resolved: any = {};
		const keys = Object.keys(column);

		// Описания слотов в колонку не копируем: ant стал бы разрешать их повторно, против своих
		for (let i = 0; i < keys.length; i += 1) {
			const key = keys[i];

			if (key !== 'slots' && key !== 'scopedSlots') {
				resolved[key] = column[key];
			}
		}

		if (column.slots) {
			this.applyPlainSlots(resolved, column.slots);
		}

		if (column.scopedSlots) {
			this.applyScopedSlots(resolved, column.scopedSlots);
		}

		if (column.children) {
			resolved.children = this.resolveColumnSlots(column.children);
		}

		/** ellipsis ставим только колонкам с шириной: одна такая колонка фиксирует раскладку всей таблицы */
		if (resolved.ellipsis === undefined
			&& resolved.dataIndex !== undefined
			&& resolved.customRender === undefined
			&& resolved.width !== undefined) {
			resolved.ellipsis = true;
		}

		return resolved;
	}

	private applyPlainSlots(resolved: any, map: { [key: string]: string }): void {
		const keys = Object.keys(map);

		for (let i = 0; i < keys.length; i += 1) {
			const key = keys[i];
			const nodes = this.$slots[map[key]];

			if (resolved[key] === undefined && nodes) {
				resolved[key] = nodes.length === 1 ? nodes[0] : nodes;
			}
		}
	}

	private applyScopedSlots(resolved: any, map: { [key: string]: string }): void {
		const keys = Object.keys(map);

		for (let i = 0; i < keys.length; i += 1) {
			const key = keys[i];
			const slot = this.$scopedSlots[map[key]];

			if (resolved[key] === undefined && slot) {
				resolved[key] = slot;
			}
		}
	}

	private renderEmptyState(): any {
		const h = this.$createElement;

		if (this.loading) {
			return h('div', { class: 'sm-mobilemon__empty' });
		}

		// Vue 2.6 проксирует и обычные слоты в `$scopedSlots`, поэтому одной
		// проверки достаточно для обеих форм записи слота `empty`.
		const slot = this.$scopedSlots.empty;

		if (slot) {
			return slot({});
		}

		return h('div', { class: 'sm-mobilemon__empty' }, [
			h('div', { class: 'sm-mobilemon__empty-title' }, [this.emptyTitleText]),
		]);
	}

	private get emptyTitleText(): string {
		return this.emptyText !== ''
			? this.emptyText
			: (this.$t('priceMonitoring.mobileMonitoring.table.empty') as string);
	}

	private formatTotal(total: number, range: number[]): string {
		const from = range && range.length > 0 ? range[0] : 0;
		const to = range && range.length > 1 ? range[1] : 0;

		return this.$t('priceMonitoring.mobileMonitoring.table.total', {
			from: from,
			to: to,
			total: total,
		}) as string;
	}

	private warnAboutBrokenExpandSetup(): void {
		if (process.env.NODE_ENV === 'production' || !this.expandable) {
			return;
		}

		if (!this.$scopedSlots.mmExpandedRow) {
			// tslint:disable-next-line:no-console
			console.warn(
				'[MmTable] expandable=true, но слот mmExpandedRow не передан: '
				+ 'ant не считает строки раскрываемыми и разворачиватель не появится.',
			);
		}

		if (this.scrollX !== null && this.scrollX !== undefined && this.scrollX !== '') {
			// tslint:disable-next-line:no-console
			console.warn(
				'[MmTable] раскрытие строк несовместимо с горизонтальной прокруткой: '
				+ 'уберите scrollX либо откажитесь от expandable.',
			);
		}
	}
}
